import { useMemo } from 'react';
import type { PropsWithChildren } from 'react';

import { useMsal } from '@azure/msal-react';

import { PermissionContext } from './PermissionContext';
import type { PermissionContextValue } from './PermissionContext';
import { evaluatePermission } from './engine';
import { ROLES } from './registry';
import type { Permissions } from './registry';
import type { CurrentUser } from './types';

const toStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const toStringClaim = (value: unknown): string => (typeof value === 'string' ? value : '');

/**
 * Builds the current user from the active MSAL account (roles + identity come
 * from the id-token claims) and exposes a typed `hasPermission` check.
 *
 * Must be rendered inside the MSAL provider. Available portal-wide so any app
 * can gate UI or actions with `usePermission`.
 */
export const PermissionProvider = ({ children }: PropsWithChildren) => {
  const { instance, accounts } = useMsal();
  const account = instance.getActiveAccount() ?? accounts.at(0) ?? null;

  const currentUser = useMemo<CurrentUser | null>(() => {
    if (!account) return null;
    const claims = account.idTokenClaims ?? {};
    return {
      mpId: toStringClaim(claims.onpremisesuserprincipalname),
      username: account.username ?? '',
      name: account.name ?? '',
      roles: toStringArray(claims.roles),
    };
  }, [account]);

  const value = useMemo<PermissionContextValue>(
    () => ({
      currentUser,
      hasPermission: (resource, action, data) => {
        if (!currentUser) return false;
        return evaluatePermission<Permissions, typeof resource>(ROLES, currentUser, resource, action, data);
      },
    }),
    [currentUser],
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
};
